import dayjs, { type Dayjs } from 'dayjs'
// types
import type { DatePreset, DatePresetBoundary } from '@/types/components/dateTime'

/**
 * Central registry of ready-made date-range presets.
 * Boundaries use the function form so they are resolved at click time and stay relative.
 * Consumers compose their own list, mixing built-ins with custom presets:
 * `:presets="[datePresets.last7Days, datePresets.thisMonth, myCustomPreset]"`
 */
export const datePresets = {
  today: {
    id: 'today',
    label: 'Today',
    shortLabel: 'Today',
    start: () => dayjs().startOf('day'),
    end: () => dayjs().endOf('day')
  },
  yesterday: {
    id: 'yesterday',
    label: 'Yesterday',
    shortLabel: 'Yesterday',
    start: () => dayjs().subtract(1, 'day').startOf('day'),
    end: () => dayjs().subtract(1, 'day').endOf('day')
  },
  last7Days: {
    id: 'last7Days',
    label: 'Last 7 days',
    shortLabel: 'Last 7d',
    start: () => dayjs().subtract(6, 'day').startOf('day'),
    end: () => dayjs().endOf('day')
  },
  last14Days: {
    id: 'last14Days',
    label: 'Last 14 days',
    shortLabel: 'Last 14d',
    start: () => dayjs().subtract(13, 'day').startOf('day'),
    end: () => dayjs().endOf('day')
  },
  last30Days: {
    id: 'last30Days',
    label: 'Last 30 days',
    shortLabel: 'Last 30d',
    start: () => dayjs().subtract(29, 'day').startOf('day'),
    end: () => dayjs().endOf('day')
  },
  last90Days: {
    id: 'last90Days',
    label: 'Last 90 days',
    shortLabel: 'Last 90d',
    start: () => dayjs().subtract(89, 'day').startOf('day'),
    end: () => dayjs().endOf('day')
  },
  thisWeek: {
    id: 'thisWeek',
    label: 'This week',
    shortLabel: 'This week',
    start: () => dayjs().startOf('week'),
    end: () => dayjs().endOf('week')
  },
  lastWeek: {
    id: 'lastWeek',
    label: 'Last week',
    shortLabel: 'Last week',
    start: () => dayjs().subtract(1, 'week').startOf('week'),
    end: () => dayjs().subtract(1, 'week').endOf('week')
  },
  thisMonth: {
    id: 'thisMonth',
    label: 'This month',
    shortLabel: 'This month',
    start: () => dayjs().startOf('month'),
    end: () => dayjs().endOf('month')
  },
  lastMonth: {
    id: 'lastMonth',
    label: 'Last month',
    shortLabel: 'Last month',
    start: () => dayjs().subtract(1, 'month').startOf('month'),
    end: () => dayjs().subtract(1, 'month').endOf('month')
  },
  thisYear: {
    id: 'thisYear',
    label: 'This year',
    shortLabel: 'This year',
    start: () => dayjs().startOf('year'),
    end: () => dayjs().endOf('year')
  },
  lastYear: {
    id: 'lastYear',
    label: 'Last year',
    shortLabel: 'Last year',
    start: () => dayjs().subtract(1, 'year').startOf('year'),
    end: () => dayjs().subtract(1, 'year').endOf('year')
  }
} satisfies Record<string, DatePreset>

/**
 * Resolves a single preset boundary to a Dayjs instance.
 * Accepts a Dayjs, an ISO date string, or a function returning a Dayjs.
 */
function resolveBoundary(boundary: DatePresetBoundary): Dayjs {
  // Function form is evaluated now so relative presets stay relative
  if (typeof boundary === 'function') {
    return boundary()
  }
  // String form is parsed leniently
  if (typeof boundary === 'string') {
    return dayjs(boundary)
  }
  return boundary
}

/**
 * Resolves a preset's boundaries to concrete Dayjs instances at call time.
 */
export function resolvePreset(preset: DatePreset): { start: Dayjs; end: Dayjs } {
  return {
    start: resolveBoundary(preset.start),
    end: resolveBoundary(preset.end)
  }
}

/**
 * Returns the stable identifier for a preset (explicit id, falling back to label).
 */
export function getPresetId(preset: DatePreset): string {
  return preset.id ?? preset.label
}

/**
 * Finds a preset in a list by its id (or label fallback).
 */
export function findPresetById(presets: DatePreset[], id: string | undefined): DatePreset | undefined {
  // No id means no preset is active
  if (!id) return undefined
  return presets.find(preset => getPresetId(preset) === id)
}
